//@flow
import * as React from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import queryString from 'query-string';

import useShouldIncludeInactiveTeams from 'common/hooks/useShouldIncludeInactiveTeams';
import useFilteredTeams from 'common/hooks/useFilteredTeams';
import { memoize } from 'common/utils/Memoizer';
import { withRouter } from 'common/components/core/router';
import { TeamEnum, TeamStatusEnum } from 'common/enums/TeamEnum';
import TimeUtils from 'common/utils/TimeUtils';
import clientSettingsSelector from 'common/redux/selectors/clientSettingsSelector';

import {
    getSelectedTourDates,
    getSelectedTourIsMultiDay
} from 'common/redux/selectors/userTour';
import { personnelBUTeamsSelector } from 'common/redux/selectors/personnelTeamsSelector';
import { isGlobalPersonnelSelector } from 'common/redux/selectors/sessionSelector';
import {
    selectedDateSelector,
    selectedDateRangeSelector,
    shiftIdSelector,
    activeTeamIdSelector,
    clientSelector as clientIdSelector,
    selectedMultiDateSelector,
    selectedTeamIdsSelector
} from 'common/redux/selectors/queryParamsSelectors';
import { shiftSelectorForHeader } from 'common/redux/selectors/shiftSelector';
import {
    getLiveViewVisibilitySettings,
    getOrderManagementSettings
} from 'common/redux/selectors/clientSelector';

import BasicEntityMgmtHeader from 'common/entity/EntityMgmtHeader';
import IntelliSortAppHeader from 'common/components/intellisort/IntelliSortAppHeader';

const appHeaderContainerFactory = ({ HeaderComponent, options = {} }) => {
    const AppHeaderContainer = props => {
        const { location, history, teams, selectedTeamIds } = props;

        const [shouldIncludeInactiveTeams] = useShouldIncludeInactiveTeams();

        // teams prop can be null, i.e in pjp as it doesn't use team selector
        const relevantTeams = useFilteredTeams(
            teams ?? [],
            options.shouldConsiderInactiveTeamsToggle ?? false
        );
        const getTeamIdToTeamMap = () => {
            const teamsMap = {};
            if (teams?.length) {
                teams.forEach(team => {
                    teamsMap[team.teamId] = team;
                });
            }
            return teamsMap;
        };
        const [teamIdToTeamMap] = React.useState(getTeamIdToTeamMap());

        const relevantSelectedTeamIds = React.useMemo(() => {
            // When we are on insights page (based on shouldConsiderInactiveTeamsToggle)
            // and shouldIncludeInactiveTeams toggle is off,
            // we need to remove inactive team ids from selected team ids
            if (
                options.shouldConsiderInactiveTeamsToggle &&
                shouldIncludeInactiveTeams
            )
                return selectedTeamIds;

            // selectedTeamIds prop can be null, i.e in pjp as it doesn't use team selector
            return (selectedTeamIds ?? []).filter(teamId => {
                const team = teamIdToTeamMap[teamId];
                if (!team) return false;
                return team.status !== TeamStatusEnum.Inactive;
            });
        }, [selectedTeamIds, shouldIncludeInactiveTeams, teamIdToTeamMap]);

        const pushToHistory = React.useCallback(
            ({ query }) => {
                const path = {
                    ...location,
                    search: queryString.stringify(query)
                };
                history.replace(path);
            },
            [location, history]
        );

        React.useLayoutEffect(() => {
            // This useEffect filters out inactive teams from url search params on pages other than insights page
            // We need to also handle inactive teams in url in LeftBar component
            // because dispatcher header doesn't use AppHeaderContainer
            const currentQuery = queryString.parse(location.search);
            const getShouldUpdateQueryParams = () => {
                if (!currentQuery.teamIds) return false;
                const isAnyTeamInactive = currentQuery.teamIds
                    .split(',')
                    .some(teamId => {
                        const team = teamIdToTeamMap[teamId];
                        if (!team) return false;
                        return team.status === TeamStatusEnum.Inactive;
                    });
                if (
                    options.shouldConsiderInactiveTeamsToggle &&
                    shouldIncludeInactiveTeams
                ) {
                    return !isAnyTeamInactive;
                }
                return isAnyTeamInactive;
            };

            if (getShouldUpdateQueryParams()) {
                const activeTeamIds = currentQuery.teamIds
                    .split(',')
                    .filter(teamId => {
                        const team = teamIdToTeamMap[teamId];
                        if (!team) return false;
                        return team.status !== TeamStatusEnum.Inactive;
                    });
                let query;
                if (activeTeamIds.length) {
                    query = _.merge({}, location.query, {
                        teamIds: activeTeamIds.join(',')
                    });
                } else query = _.omit(location.query, 'teamIds');
                pushToHistory({ query });
            }
        }, [
            location,
            pushToHistory,
            teamIdToTeamMap,
            shouldIncludeInactiveTeams
        ]);

        const onShiftChange = React.useCallback(
            shiftId => {
                const query = _.merge({}, location.query, {
                    shiftId
                });
                pushToHistory({ query });
            },
            [location, pushToHistory]
        );

        const clearUrlDate = React.useCallback(() => {
            const query = _.omit(location.query, ['date']);
            pushToHistory({ query });
        }, [location, pushToHistory]);

        const clearUrlDateRange = React.useCallback(() => {
            const query = _.omit(location.query, [
                'startDate',
                'endDate',
                'range'
            ]);
            pushToHistory({ query });
        }, [location, pushToHistory]);

        const onDateChange = React.useCallback(
            date => {
                const dateString = TimeUtils.formatWithEnglishLocale(date);
                const query = _.merge({}, location.query, {
                    date: dateString
                });
                pushToHistory({ query });
            },
            [location, pushToHistory]
        );

        const onTeamChange = React.useCallback(
            ({ value }) => {
                const query = _.merge({}, location.query, {
                    teamIds: value
                });
                pushToHistory({ query });
            },
            [location, pushToHistory]
        );

        const onMultiDayDateChange = React.useCallback(
            ({ startDate, endDate, range }) => {
                const queries = _.omit(location.query, [
                    'startDate',
                    'endDate',
                    'range'
                ]);

                if (range) {
                    queries.range = range;
                } else if (startDate || endDate) {
                    const startDateString = TimeUtils.formatWithEnglishLocale(
                        startDate
                    );
                    queries.startDate = startDateString;
                    if (
                        endDate &&
                        (!startDate.isSame(endDate, 'day') ||
                            options.allowSameEndDate)
                    ) {
                        const endDateString = TimeUtils.formatWithEnglishLocale(
                            endDate
                        );
                        queries.endDate = endDateString;
                    }
                }

                pushToHistory({ query: queries });
            },
            [location, pushToHistory]
        );

        const onTeamSelect = React.useCallback(
            teamIds => {
                let query;
                /* If both selectedTeamIds array & relevantTeams array is equal,
                it means we have all the checkbox selected
                in that case we will remove the teamIds params from URL.
            */
                if (!teamIds || teamIds.length === relevantTeams.length) {
                    query = _.omit(location.query, 'teamIds');
                } else if (teamIds.length === 0) {
                    query = _.merge({}, location.query, {
                        teamIds: TeamEnum.NoTeam
                    });
                } else {
                    query = _.merge({}, location.query, {
                        teamIds: teamIds.join(',')
                    });
                }
                pushToHistory({ query });
            },
            [location, relevantTeams, pushToHistory]
        );

        return (
            <HeaderComponent
                {...props}
                {...options}
                teams={relevantTeams}
                selectedTeamIds={relevantSelectedTeamIds}
                onShiftChange={onShiftChange}
                onTeamSelect={onTeamSelect}
                onTeamChange={onTeamChange}
                onDateChange={onDateChange}
                onDatesChange={onMultiDayDateChange}
                clearUrlDate={clearUrlDate}
                clearUrlDateRange={clearUrlDateRange}
            />
        );
    };
    return AppHeaderContainer;
};

const convertTeamsToJSON = memoize(teams => {
    let teamsJson = [];
    if (teams) {
        teamsJson = Object.values(teams.toJS());
    }
    return teamsJson;
});

const mapStateToProps = (state, props) => {
    const shifts = shiftSelectorForHeader(state, props);
    const selectedShiftId = shiftIdSelector(state, props);
    const clientId = clientIdSelector(state, props);
    const selectedDate = selectedDateSelector(state, props);
    const {
        startDate: selectedStartDateString,
        endDate: selectedEndDateString
    } = selectedMultiDateSelector(state, props);
    const selectedStartDate = selectedStartDateString;
    const selectedEndDate = selectedEndDateString
        ? selectedEndDateString
        : null;
    const selectedDateRange = selectedDateRangeSelector(state, props);
    const teams = personnelBUTeamsSelector(state, props);
    const selectedTeamIds = selectedTeamIdsSelector(state, props);
    const selectedTeamId = activeTeamIdSelector(state, props);
    const clientSettings = clientSettingsSelector(state, props);
    const isCreateTaskEnabled = clientSettings
        ? clientSettings.enableCreateTask
        : false;
    const tourDates = getSelectedTourDates(state, props);
    const isMultiDay = getSelectedTourIsMultiDay(state, props);
    const liveViewVisibilitySettings = getLiveViewVisibilitySettings(
        state,
        props
    );
    const isGlobalPersonnel = isGlobalPersonnelSelector(state, props);
    const orderManagementSettings = getOrderManagementSettings(state, props);
    return {
        teams: convertTeamsToJSON(teams),
        shifts: shifts.toJS(),
        selectedShiftId,
        selectedDate,
        selectedStartDate,
        selectedEndDate,
        selectedDateRange,
        history: props.history,
        selectedTeamIds,
        isGlobalPersonnel,
        selectedTeamId,
        clientId,
        isCreateTaskEnabled,
        tourDates,
        isMultiDay,
        liveViewVisibilitySettings,
        orderManagementSettings
    };
};

export const createAppHeader = ({ HeaderComponent, options }) => {
    const AppHeader = appHeaderContainerFactory({ HeaderComponent, options });
    return withRouter(connect(mapStateToProps)(AppHeader));
};

const EntityMgmtHeader = createAppHeader({
    HeaderComponent: BasicEntityMgmtHeader
});

const IntelliSortHeader = createAppHeader({
    HeaderComponent: IntelliSortAppHeader
});

const TerritoryPlanningHeader = createAppHeader({
    HeaderComponent: BasicEntityMgmtHeader
});

export { EntityMgmtHeader, IntelliSortHeader, TerritoryPlanningHeader };
